import { Box, Chip, type ChipProps, Grid, Stack, Typography } from "@mui/material";
import { tileSx } from "Components/_Shared/Tile";
import type { Amount, ContractListItemStep, ContractSolutionLabel } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { CONTRACT_COVERAGE_LABEL_RESOURCE_KEY, CONTRACT_STEP_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { formatCurrency, formatCurrencyWithoutCode } from "Infrastructure/Utils/CurrencyUtils";
import { getAmountFrequencyTranslation } from "Infrastructure/Utils/LanguageUtils";
import { useContext } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";

type Props = {
  readonly guid: string;
  readonly name: string;
  readonly insuranceAmount: Amount | null;
  readonly step: ContractListItemStep;
  readonly labels: ContractSolutionLabel[];
  readonly onEdit: (guid: string) => void;
};

const stepColorMap: Partial<Record<ContractListItemStep, ChipProps["color"]>> = {
  create_recommended: "success",
  keep: "success",
  modify: "warning",
  cancel: "error",
};

const StatusLabel: React.FC<Pick<Props, "step">> = ({ step }) => {
  const { t } = useResource();

  return (
    <Chip
      size="small"
      color={stepColorMap[step] ?? "default"}
      label={t(Resources.AnalysisDetail.AssetsAndContracts.ProposedSolutionStepTypesShort[CONTRACT_STEP_RESOURCE_KEY[step]])}
      sx={{ ml: Spacing.M }}
    />
  );
};

export const ContractTile: React.FC<Props> = ({ guid, name, insuranceAmount, step, labels, onEdit }) => {
  const { t } = useResource();

  const { readOnly } = useContext(AnalysisDetailContext);

  return (
    <Stack
      direction="row"
      justifyContent="flex-start"
      alignItems="flex-start"
      gap={Spacing.L}
      p={Spacing.XL}
      sx={[tileSx, { cursor: readOnly ? "default" : "pointer" }]}
      onClick={() => {
        if (readOnly) return;
        onEdit(guid);
      }}
    >
      <Grid container width="100%">
        <Grid size={9}>
          <Stack spacing={Spacing.S}>
            <Box display="flex" alignItems={"center"}>
              <Typography variant="body2" fontWeight={600}>
                {name}
              </Typography>
              <StatusLabel step={step} />
            </Box>
            <Box>
              {labels
                .filter((label) => label.amount === null || label.amount.value > 0)
                .map((label, index, visibleLabels) => {
                  const resourceKey = CONTRACT_COVERAGE_LABEL_RESOURCE_KEY[label.label];

                  return (
                    <Box key={`${label.label}-${index}`} display="inline-block">
                      <Typography display="inline" variant="caption" color="secondary">
                        {resourceKey ? t(Resources.AnalysisDetail.AssetsAndContracts.Form[resourceKey]) : label.label}
                      </Typography>
                      &nbsp;
                      {label.amount !== null && (
                        <Typography display="inline" variant="caption" fontWeight={500}>
                          {formatCurrency(label.amount?.value, label.amount?.currencyCode)}
                        </Typography>
                      )}
                      {visibleLabels.length - 1 !== index && (
                        <Box component="span" display="inline-flex" alignItems="center" mx={Spacing.M} sx={{ verticalAlign: "middle" }}>
                          <Typography variant="caption" color="secondary" sx={{ fontSize: "100%", lineHeight: 1 }}>
                            &bull;
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
            </Box>
          </Stack>
        </Grid>
        <Grid size={3} display="flex" alignItems="flex-end" flexDirection="column">
          <Box display="flex" flexWrap="wrap" columnGap={0.5} rowGap={0.25} alignItems={"flex-end"} justifyContent="flex-end">
            <Typography variant="body2" fontWeight={500} sx={{ whiteSpace: "nowrap", flexBasis: { xs: "auto" } }}>
              {formatCurrencyWithoutCode(insuranceAmount?.value ?? 0)}
            </Typography>
            <Box display="flex" columnGap={0.5} sx={{ whiteSpace: "nowrap" }}>
              <Typography variant="body2" color="secondary">
                {t(Resources.Common.CZKSuffix)}
              </Typography>
              {insuranceAmount && (
                <Typography variant="body2" color="secondary">
                  /{getAmountFrequencyTranslation(insuranceAmount.frequency, t)}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
};
